import { CommonModule } from '@angular/common';
import { ModuleWithProviders, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AumicoCoreModule, AumicoPatternModule } from '@modeso/aumico-lib-core-fe';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';

import { AumicoTemplatesConfig, AumicoTemplatesConfigService } from './aumico-templates.config';
import { AccountMappingChildComponent } from './components/account-mapping/account-mapping-child/account-mapping-child-form.component';
import { ChangeCaptionFormDialog } from './components/account-mapping/change-caption-form/change-caption-form.dialog';
import { GroupAccountMappingComponent } from './components/account-mapping/group-account-mapping/group-account-mapping.component';
import { SubFliFormDialog } from './components/account-mapping/sub-fli-form/sub-fli-form.dialog';
import { TemplateAccountMappingComponent } from './components/account-mapping/template-account-mapping/template-account-mapping-form.component';
import { AdjustmentsReclassesFormComponent } from './components/adjustments-reclasses-form/adjustments-reclasses-form.component';
import { AdjustmentsReclassesGroupFormComponent } from './components/adjustments-reclasses-form/adjustments-reclasses-group-form/adjustments-reclasses-group-form.component';
import { BaseComponent } from './components/base.component';
import { AccountFormComponent } from './components/editable-account-form/account-form/account-form.component';
import { EditableAccountFormComponent } from './components/editable-account-form/editable-account-form.component';
import { FinancialLineItemsListComponent } from './components/groups-items/financial-line-items-list/financial-line-items-list.component';
import { SubtotalFormComponent } from './components/groups-items/subtotal-form/subtotal-form.component';
import { GroupFormComponent } from './components/groups/group-form/group-form.component';
import { GroupListComponent } from './components/groups/group-list/group-list.component';

import { MatTableModule } from '@angular/material/table';
import { I18NextModule } from 'angular-i18next';
import { FliFormDialogComponent } from './components/account-mapping/fli-form/fli-form.dialog';
import { GroupFormDialogComponent } from './components/account-mapping/mapping-group-form/group-form.dialog';
import { SubtotalFormDialogComponent } from './components/account-mapping/mapping-subtotal-form/subtotal-form.dialog';

import { DragDropModule } from '@angular/cdk/drag-drop';
import { AumicoComponentsModule } from '@modeso/aumico-components';
import { AutomaticTransferOfAnnualResultService } from '../public-api';
import { AddSubtotalFormDialogComponent } from './components/account-mapping/mapping-add-subtotal-form/add-subtotal-form.dialog';
import { MenuListForMappingComponent } from './components/account-mapping/menu-list-for-mapping/menu-list-for-mapping.component';
import { SwitchAccountsDialogComponent } from './components/account-mapping/switch-accounts-form/switch-accounts-form.dialog';
import { AdminSubFliFormDialog } from './components/admin-sub-fli-form-dialog/sub-fli-form-dialog.component';
import { DataGridComponent } from './components/data-grid/data-grid.component';
import { DuplicateAccountFormDialogComponent } from './components/duplicate-account-form-dialog/duplicate-account-form.dialog';
import { EditGroupsTranslationsComponent } from './components/edit-groups-translations/edit-groups-translations.component';
import { EmptyDataGridFeedbackComponent } from './components/empty-data-grid-feedback/empty-data-grid-feedback.component';
import { HeaderFormComponent } from './components/header-form/header-form.component';
import { EditCellDirective } from './directives/edit-cell.directive';
import { MenuSelectionBackgroundDirective } from './directives/menu-selection-background.directive';
import { TemplatesEffects } from './effects/templates.effects';
import { AccountMappingComponent } from './pages/account-mapping/account-mapping-form.component';
import { TemplatesFormComponent } from './pages/addOrUpdate/templates-form.component';
import { GroupViewComponent } from './pages/group-view/group-view.component';
import { TemplatesListComponent } from './pages/list/templates-list.component';
import * as fromAumicoTemplatesReducer from './reducers/aumico-templates.reducer';
import { AumicoTemplatesService } from './services/templates.service';

const EXPORTED_DECLARATIONS = [
    TemplatesListComponent,
    TemplatesFormComponent,
    GroupListComponent,
    GroupFormComponent,
    GroupViewComponent,
    GroupAccountMappingComponent,
    SubFliFormDialog,
    ChangeCaptionFormDialog,
    SwitchAccountsDialogComponent,
    FliFormDialogComponent,
    TemplateAccountMappingComponent,
    AccountMappingChildComponent,
    AccountMappingComponent,
    AdjustmentsReclassesFormComponent,
    AdjustmentsReclassesGroupFormComponent,
    EditGroupsTranslationsComponent,
    SubtotalFormDialogComponent,
    AddSubtotalFormDialogComponent,
    AdminSubFliFormDialog,
    GroupFormDialogComponent,
    SubtotalFormComponent,
    FinancialLineItemsListComponent,
    BaseComponent,
    EditableAccountFormComponent,
    AccountFormComponent,
    HeaderFormComponent,
    MenuListForMappingComponent,
    DuplicateAccountFormDialogComponent,
    DataGridComponent,
    EmptyDataGridFeedbackComponent,
];

const ANGULAR_MATERIAL = [
    MatCheckboxModule,
    MatDialogModule,
    MatInputModule,
    MatFormFieldModule,
    MatIconModule,
    MatGridListModule,
    MatMenuModule,
    MatSelectModule,
    MatExpansionModule,
    MatTabsModule,
    MatListModule,
    MatTooltipModule,
    MatTableModule,
];

@NgModule({
    declarations: [EXPORTED_DECLARATIONS, EditCellDirective, MenuSelectionBackgroundDirective],
    providers: [
        {
            provide: MatDialogRef,
            useValue: {},
        },
    ],
    imports: [
        CommonModule,
        I18NextModule,
        AumicoCoreModule,
        FormsModule,
        ReactiveFormsModule,
        StoreModule.forFeature(
            fromAumicoTemplatesReducer.aumicoTemplatesFeatureKey,
            fromAumicoTemplatesReducer.reducers,
        ),
        EffectsModule.forFeature([TemplatesEffects]),
        ANGULAR_MATERIAL,
        AumicoPatternModule,
        MatIconModule,
        DragDropModule,
        AumicoComponentsModule,
    ],
    exports: [EXPORTED_DECLARATIONS],
})
export class AumicoTemplatesModule {
    static forRoot(config: AumicoTemplatesConfig): ModuleWithProviders<AumicoTemplatesModule> {
        return {
            ngModule: AumicoTemplatesModule,
            providers: [
                AumicoTemplatesService,
                AutomaticTransferOfAnnualResultService,
                {
                    provide: AumicoTemplatesConfigService,
                    useValue: config,
                },
            ],
        };
    }
}
